import { useEffect, useRef, useState } from 'react';
import { formatDateTime } from '../lib/format.js';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  AppBar,
  Box,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Avatar,
  IconButton,
  InputBase,
  Tooltip,
  Badge,
  Stack,
  Menu,
  MenuItem,
  Divider,
  Button,
  Alert,
  useMediaQuery,
} from '@mui/material';
import { alpha, type Theme } from '@mui/material/styles';
import SpaceDashboardIcon from '@mui/icons-material/SpaceDashboard';
import SensorsIcon from '@mui/icons-material/Sensors';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import HubIcon from '@mui/icons-material/Hub';
import DataObjectIcon from '@mui/icons-material/DataObject';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecordOutlined';
import ShieldIcon from '@mui/icons-material/Shield';
import BugReportIcon from '@mui/icons-material/BugReport';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import InsightsIcon from '@mui/icons-material/Insights';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import GridOnIcon from '@mui/icons-material/GridOn';
import PeopleIcon from '@mui/icons-material/People';
import ReceiptIcon from '@mui/icons-material/Receipt';
import ApartmentIcon from '@mui/icons-material/Apartment';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import PaymentsIcon from '@mui/icons-material/Payments';
import GppGoodIcon from '@mui/icons-material/GppGood';
import ForumIcon from '@mui/icons-material/ForumOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import ExtensionIcon from '@mui/icons-material/Extension';
import TuneIcon from '@mui/icons-material/Tune';
import PsychologyIcon from '@mui/icons-material/Psychology';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import LogoutIcon from '@mui/icons-material/Logout';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import SearchIcon from '@mui/icons-material/Search';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import MenuIcon from '@mui/icons-material/Menu';
import { useAuth } from '../auth/AuthContext.js';
import { useMe } from '../api/hooks.js';
import { actAsStore } from '../api/client.js';
import { api } from '../api/client.js';
import { useColorMode } from '../theme/ColorMode.js';
import { useT } from '../i18n/useT.js';
import { LangToggle } from '../i18n/LangToggle.js';
import { HeaderProjectPicker } from './HeaderProjectPicker.js';
import type { Copy } from '../i18n/copy/index.js';
import { useUsage, useLicense } from '../pages/BillingPage.js';

const DRAWER_WIDTH = 244;

type NavKey = keyof Copy['shell']['nav'];
type GroupKey = keyof Copy['shell']['groups'];

interface NavItem {
  /** Dictionary key, not a finished label — resolved at render time so
      switching language redraws the sidebar instead of needing a reload. */
  key: NavKey;
  path: string;
  icon: JSX.Element;
  adminOnly?: boolean;
  superAdminOnly?: boolean;
}
interface NavGroup {
  key: GroupKey;
  items: NavItem[];
}

const GROUPS: NavGroup[] = [
  {
    key: 'core',
    items: [
      { key: 'dashboard', path: '/dashboard', icon: <SpaceDashboardIcon /> },
      { key: 'live', path: '/live', icon: <SensorsIcon /> },
      { key: 'sessions', path: '/sessions', icon: <PlayCircleOutlineIcon /> },
      { key: 'tickets', path: '/tickets', icon: <ConfirmationNumberIcon /> },
      { key: 'signals', path: '/signals', icon: <LocalFireDepartmentIcon /> },
      { key: 'errors', path: '/errors', icon: <BugReportIcon /> },
      { key: 'alerts', path: '/alerts', icon: <NotificationsActiveIcon /> },
      { key: 'summaries', path: '/summaries', icon: <AutoAwesomeIcon /> },
      { key: 'ask', path: '/ask', icon: <ChatBubbleOutlineIcon /> },
    ],
  },
  {
    key: 'analytics',
    items: [
      { key: 'supportAnalytics', path: '/support-analytics', icon: <SupportAgentIcon /> },
      { key: 'userBehavior', path: '/user-behavior', icon: <InsightsIcon /> },
      { key: 'funnel', path: '/analytics/funnel', icon: <FilterAltIcon /> },
      { key: 'heatmaps', path: '/analytics/heatmaps', icon: <GridOnIcon /> },
    ],
  },
  {
    key: 'admin',
    items: [
      { key: 'users', path: '/users', icon: <PeopleIcon />, adminOnly: true },
      { key: 'apiKeys', path: '/projects', icon: <VpnKeyIcon /> },
      { key: 'apiAccess', path: '/api-access', icon: <HubIcon />, adminOnly: true },
      { key: 'sourcemaps', path: '/sourcemaps', icon: <DataObjectIcon />, adminOnly: true },
      { key: 'integrations', path: '/integrations', icon: <ExtensionIcon /> },
      { key: 'ai', path: '/ai-settings', icon: <PsychologyIcon />, adminOnly: true },
      { key: 'configuration', path: '/configuration', icon: <TuneIcon />, adminOnly: true },
      { key: 'security', path: '/security', icon: <ShieldIcon />, adminOnly: true },
      { key: 'recording', path: '/recording', icon: <FiberManualRecordIcon />, adminOnly: true },
      { key: 'billing', path: '/billing', icon: <ReceiptIcon /> },
      { key: 'help', path: '/help', icon: <HelpOutlineIcon /> },
    ],
  },
  {
    key: 'platform',
    items: [
      { key: 'onboarding', path: '/admin/onboarding', icon: <RocketLaunchIcon />, superAdminOnly: true },
      { key: 'companies', path: '/admin/companies', icon: <ApartmentIcon />, superAdminOnly: true },
      { key: 'platformUsers', path: '/admin/users', icon: <PeopleAltIcon />, superAdminOnly: true },
      { key: 'plans', path: '/admin/plans', icon: <PaymentsIcon />, superAdminOnly: true },
      { key: 'email', path: '/admin/email', icon: <MailOutlineIcon />, superAdminOnly: true },
      { key: 'demoRequests', path: '/admin/demo-requests', icon: <ForumIcon />, superAdminOnly: true },
      { key: 'recaptcha', path: '/admin/recaptcha', icon: <GppGoodIcon />, superAdminOnly: true },
    ],
  },
];

export function Layout() {
  const t = useT().shell;
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();
  const { data: me } = useMe();
  const { mode, toggle } = useColorMode();
  const org = me?.organizations[0];
  const isAdmin = org?.role === 'OWNER' || org?.role === 'ADMIN';
  const initials = (me?.fullName || me?.email || '?')
    .split(/[\s@.]+/)
    .slice(0, 2)
    .map((s) => s.charAt(0).toUpperCase())
    .join('');

  // ⌘K / Ctrl+K focuses the global search.
  const searchRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Below md the sidebar would eat two-thirds of a phone screen, so it becomes
  // an overlay opened from the toolbar. Navigating closes it — otherwise the
  // destination is hidden behind the menu you just used.
  const compact = useMediaQuery((t: Theme) => t.breakpoints.down('md'));
  const [navOpen, setNavOpen] = useState(false);
  useEffect(() => setNavOpen(false), [location.pathname]);

  return (
    <Box sx={{ display: 'flex' }}>
      {/* Super-admin impersonation: an unmissable strip while acting as a tenant. */}
      {org?.impersonated && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: (t) => t.zIndex.drawer + 2,
            bgcolor: '#6d28d9',
            color: '#fff',
            px: 2,
            py: 0.75,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            fontSize: 13,
          }}
        >
          <ShieldIcon sx={{ fontSize: 16 }} />
          <span>
            {t.impersonation.prefix} <b>{org.name}</b>.
          </span>
          <Box sx={{ flex: 1 }} />
          <Button
            size="small"
            variant="outlined"
            sx={{ color: '#fff', borderColor: 'rgba(255,255,255,.6)', py: 0, textTransform: 'none' }}
            onClick={() => {
              actAsStore.clear();
              window.location.href = '/admin/companies';
            }}
          >
            {t.impersonation.exit}
          </Button>
        </Box>
      )}
      <AppBar position="fixed" sx={{ zIndex: (t) => t.zIndex.drawer + 1 }}>
        <Toolbar sx={{ gap: { xs: 1, md: 2 } }}>
          {compact && (
            <IconButton edge="start" color="inherit" aria-label={t.bar.openNav} onClick={() => setNavOpen(true)}>
              <MenuIcon />
            </IconButton>
          )}
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            onClick={() => navigate('/')}
            sx={{
              // Aligns with the sidebar on desktop; on a phone it only takes
              // the room the wordmark needs so the actions still fit.
              width: { xs: 'auto', md: DRAWER_WIDTH - 32 },
              flexShrink: 0,
              cursor: 'pointer',
              userSelect: 'none',
              '&:hover': { opacity: 0.85 },
            }}
          >
            <PlayCircleIcon sx={{ color: 'primary.main' }} />
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              Replay<span style={{ color: '#2ec2b3' }}>Ops</span>
            </Typography>
          </Stack>

          {/* The search field needs real width to be usable; on a phone it
              would crowd out the actions, so it drops out below md. */}
          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              alignItems: 'center',
              gap: 1,
              flex: 1,
              maxWidth: 460,
              px: 1.5,
              height: 40,
              borderRadius: 2,
              border: 1,
              borderColor: 'divider',
              bgcolor: (t) => alpha(t.palette.text.primary, 0.03),
            }}
          >
            <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
            <InputBase inputRef={searchRef} placeholder={t.bar.search} sx={{ flex: 1, fontSize: 14 }} />
            <Box
              sx={{
                fontSize: 11,
                fontFamily: 'ui-monospace, monospace',
                color: 'text.secondary',
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                px: 0.6,
                py: 0.1,
              }}
            >
              ⌘K
            </Box>
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          <HeaderProjectPicker orgId={org?.id} />
          <Box sx={{ width: 8 }} />
          <LangToggle sx={{ display: { xs: 'none', sm: 'flex' } }} />
          <Tooltip title={mode === 'light' ? t.bar.darkMode : t.bar.lightMode}>
            <IconButton onClick={toggle} color="inherit" sx={{ display: { xs: 'none', sm: 'inline-flex' } }}>
              {mode === 'light' ? <DarkModeIcon /> : <LightModeIcon />}
            </IconButton>
          </Tooltip>
          <NotificationBell
            orgId={org?.id}
            onOpenAlerts={() => navigate('/alerts')}
            onOpenUsage={() => navigate('/billing')}
            t={t}
          />
          <Tooltip title={me?.email ?? ''}>
            <Avatar sx={{ width: 32, height: 32, ml: 0.5, bgcolor: 'primary.main', fontSize: 13, fontWeight: 700 }}>
              {initials || '?'}
            </Avatar>
          </Tooltip>
        </Toolbar>
      </AppBar>

      <Drawer
        variant={compact ? 'temporary' : 'permanent'}
        open={compact ? navOpen : true}
        onClose={() => setNavOpen(false)}
        // Keep the nav mounted so opening it on a phone is instant.
        ModalProps={{ keepMounted: true }}
        sx={{
          width: { xs: 0, md: DRAWER_WIDTH },
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: { width: DRAWER_WIDTH, boxSizing: 'border-box' },
        }}
      >
        <Toolbar />
        <Box sx={{ overflow: 'auto', display: 'flex', flexDirection: 'column', height: '100%', py: 1 }}>
          {/* Org switcher */}
          <Box sx={{ mx: 1, mb: 1 }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ px: 1.5, py: 1, borderRadius: 2, border: 1, borderColor: 'divider' }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" noWrap sx={{ fontWeight: 700 }}>
                  {org?.name ?? '—'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {org?.role}
                </Typography>
              </Box>
              <UnfoldMoreIcon fontSize="small" sx={{ color: 'text.secondary' }} />
            </Stack>
          </Box>

          {GROUPS.map((group) => {
            const items = group.items.filter(
              (n) => (!n.adminOnly || isAdmin) && (!n.superAdminOnly || me?.isSuperAdmin),
            );
            if (!items.length) return null;
            return (
              <Box key={group.key} sx={{ mt: 1 }}>
                <Typography
                  variant="caption"
                  sx={{ px: 3, color: 'text.secondary', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}
                >
                  {t.groups[group.key]}
                </Typography>
                <List sx={{ px: 0, py: 0.5 }}>
                  {items.map((item) => {
                    const selected = location.pathname.startsWith(item.path);
                    return (
                      <ListItemButton key={item.path} selected={selected} onClick={() => navigate(item.path)}>
                        <ListItemIcon sx={{ minWidth: 36, color: selected ? 'primary.main' : 'text.secondary' }}>
                          {item.icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={t.nav[item.key]}
                          primaryTypographyProps={{ fontWeight: selected ? 700 : 500, fontSize: 14 }}
                        />
                      </ListItemButton>
                    );
                  })}
                </List>
              </Box>
            );
          })}

          <Box sx={{ flexGrow: 1 }} />
          <Box sx={{ px: 2, py: 1.5, borderTop: 1, borderColor: 'divider' }}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: 13, fontWeight: 700 }}>
                {initials || '?'}
              </Avatar>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
                  {me?.fullName || me?.email?.split('@')[0]}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                  {me?.email}
                </Typography>
              </Box>
              <Tooltip title={t.bar.logOut}>
                <IconButton size="small" onClick={logout}>
                  <LogoutIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </Box>
        </Box>
      </Drawer>

      {/* minWidth:0 lets wide children (tables, charts) scroll inside their own
          container instead of stretching the page into a sideways scroll. */}
      <Box component="main" sx={{ flexGrow: 1, minWidth: 0, p: { xs: 2, md: 3 }, mt: 8, maxWidth: 1500 }}>
        {me && !me.emailVerified && <VerifyEmailBanner email={me.email} t={t} />}
        <LicenseBanner isAdmin={isAdmin} onOpen={() => navigate('/billing')} t={t} />
        {isAdmin && <LimitBanner orgId={org?.id} onOpen={() => navigate('/billing')} t={t} />}
        <Outlet />
      </Box>
    </Box>
  );
}

/**
 * A package limit at 80% or full, for the people who can buy an add-on.
 * Warning at 80%, error at 100%; one line per limit, most urgent first, and
 * nothing at all below 80%.
 */
function LimitBanner({ orgId, onOpen, t }: { orgId?: string; onOpen: () => void; t: Copy['shell'] }) {
  const usage = useUsage(orgId);
  const lines = (usage.data?.lines ?? [])
    .filter((l) => l.level > 0 && l.limit !== null)
    .sort((a, b) => b.level - a.level);
  if (lines.length === 0) return null;
  return (
    <Stack spacing={1} sx={{ mb: 2 }}>
      {lines.map((l) => (
        <Alert
          key={l.metric}
          // Only sessions stop at the limit. A full project or tool count stops
          // nothing that runs, it only means the next one needs an add-on.
          severity={l.level === 100 ? (l.metric === 'sessions' ? 'error' : 'info') : 'warning'}
          action={
            <Button color="inherit" size="small" onClick={onOpen}>
              {t.limits.cta}
            </Button>
          }
        >
          {t.limits.banner(l.metric, l.level, l.used.toLocaleString(), l.limit!.toLocaleString())}
        </Alert>
      ))}
    </Stack>
  );
}

/**
 * The on-prem licence, when it needs attention. Administrators see every
 * stage, from a renewal coming up to the grace running out; members see only
 * that recording has stopped, which is the one thing that changes for them.
 * Nothing on the cloud, where there is no licence.
 */
function LicenseBanner({ isAdmin, onOpen, t }: { isAdmin: boolean; onOpen: () => void; t: Copy['shell'] }) {
  const { data } = useLicense();
  if (!data || data.mode === 'cloud') return null;
  const L = t.license;
  const date = (iso: string) => new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' });
  let severity: 'info' | 'warning' | 'error';
  let text: string;
  if (data.mode === 'licensed') {
    if (data.expiresInDays === null || !isAdmin || !data.license.expires) return null;
    severity = 'info';
    text = L.expiring(data.expiresInDays, date(data.license.expires));
  } else if (data.mode === 'stopped') {
    severity = 'error';
    text = L.stopped;
  } else {
    if (!isAdmin) return null;
    severity = 'warning';
    const until = date(data.graceEndsAt);
    text =
      data.cause === 'expired' && data.license?.expires
        ? L.expired(date(data.license.expires), data.daysLeft, until)
        : data.cause === 'invalid'
          ? L.invalid(data.reason ?? '', data.daysLeft, until)
          : L.none(data.daysLeft, until);
  }
  return (
    <Alert
      severity={severity}
      sx={{ mb: 2 }}
      action={
        isAdmin ? (
          <Button color="inherit" size="small" onClick={onOpen}>
            {t.limits.cta}
          </Button>
        ) : undefined
      }
    >
      {text}
    </Alert>
  );
}

/** Dismissable-per-session nudge shown until the user confirms their email. */
function VerifyEmailBanner({ email, t }: { email: string; t: Copy['shell'] }) {
  const [sent, setSent] = useState(false);
  const resend = useMutation({
    mutationFn: async () => api.post('/v1/auth/resend-verification'),
    onSuccess: () => setSent(true),
  });
  return (
    <Alert
      severity="warning"
      sx={{ mb: 2 }}
      action={
        <Button color="inherit" size="small" disabled={resend.isPending || sent} onClick={() => resend.mutate()}>
          {sent ? t.verifyBanner.sent : resend.isPending ? t.verifyBanner.sending : t.verifyBanner.resend}
        </Button>
      }
    >
      {t.verifyBanner.body} <b>{email}</b> {t.verifyBanner.bodyTail}
    </Alert>
  );
}

interface Notification {
  id: string;
  /** null for a package-limit notice, which no rule fired. */
  ruleName: string | null;
  planLimit: { metric: string; level: number; limit: number } | null;
  message: string;
  createdAt: string;
}

/** Top-bar bell: unread alert count + a popover of recent alerts. */
function NotificationBell({
  orgId,
  onOpenAlerts,
  onOpenUsage,
  t,
}: {
  orgId?: string;
  onOpenAlerts: () => void;
  onOpenUsage: () => void;
  t: Copy['shell'];
}) {
  const qc = useQueryClient();
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);

  const notifications = useQuery({
    enabled: !!orgId,
    queryKey: ['notifications', orgId],
    queryFn: async () =>
      (await api.get(`/v1/orgs/${orgId}/alerts/notifications`)).data as {
        unreadCount: number;
        items: Notification[];
      },
    refetchInterval: 30_000,
  });

  const markRead = useMutation({
    mutationFn: async () => api.post(`/v1/orgs/${orgId}/alerts/notifications/read`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications', orgId] }),
  });

  const count = notifications.data?.unreadCount ?? 0;
  const items = notifications.data?.items ?? [];

  return (
    <>
      <Tooltip title={t.nav.alerts}>
        <IconButton color="inherit" onClick={(e) => setAnchor(e.currentTarget)}>
          <Badge color="error" badgeContent={count} max={99}>
            {count > 0 ? <NotificationsActiveIcon /> : <NotificationsNoneIcon />}
          </Badge>
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchor}
        open={!!anchor}
        onClose={() => setAnchor(null)}
        slotProps={{ paper: { sx: { width: 360, maxWidth: '90vw' } } }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, py: 1 }}>
          <Typography variant="subtitle2" fontWeight={700}>{t.notifications.title}</Typography>
          {count > 0 && (
            <Button size="small" onClick={() => markRead.mutate()} disabled={markRead.isPending}>
              {t.notifications.markAllRead}
            </Button>
          )}
        </Stack>
        <Divider />
        {items.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
            {t.notifications.empty}
          </Typography>
        )}
        {items.map((n) => (
          <MenuItem
            key={n.id}
            onClick={() => { setAnchor(null); (n.planLimit ? onOpenUsage : onOpenAlerts)(); }}
            sx={{ whiteSpace: 'normal', alignItems: 'flex-start', py: 1 }}
          >
            <Box>
              <Typography variant="body2" fontWeight={600}>{n.planLimit ? t.limits.title : n.ruleName}</Typography>
              <Typography variant="caption" color="text.secondary">
                {n.planLimit ? t.limits.notice(n.planLimit.metric, n.planLimit.level) : n.message}
              </Typography>
              <Typography variant="caption" color="text.disabled" sx={{ display: 'block' }}>
                {formatDateTime(n.createdAt)}
              </Typography>
            </Box>
          </MenuItem>
        ))}
        <Divider />
        <MenuItem onClick={() => { setAnchor(null); onOpenAlerts(); }} sx={{ justifyContent: 'center' }}>
          <Typography variant="body2" color="primary">{t.notifications.viewAll}</Typography>
        </MenuItem>
      </Menu>
    </>
  );
}
